/**
 * LeadsTable - the Leads page's Table view (Paul, L-11): the SAME cards the
 * board shows (views/LeadsView filters them once for both views), one row per
 * lead, sortable by any column. Export writes exactly the rows on screen, in
 * their order. Clicking a row opens the account, like clicking a card.
 *
 * Row shape, sorting and the CSV live in components/crm/leadsBoard.js.
 */
import { useMemo, useState } from 'react';
import { Icon } from '../ui/Icon';
import { STAGE_COLORS } from '../../data/crmStages';
import { fmtDate } from '../../utils/format';
import { exportCsv } from '../../utils/exportCsv';
import { TABLE_COLUMNS, tableRow, sortRows, csvRows } from './leadsBoard';

const tag = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 4,
  fontSize: 8.5,
  fontWeight: 800,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};
const th = {
  padding: '9px 12px',
  textAlign: 'left',
  fontSize: 9,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #ECECEC',
  background: '#FAFBF9',
};
const td = { padding: '9px 12px', fontSize: 11, color: '#333', borderBottom: '1px solid #F4F4F4', verticalAlign: 'top' };

export default function LeadsTable({ cards, onOpen, fileName = 'leads.csv' }) {
  const [sort, setSort] = useState({ key: 'account', dir: 'asc' });
  const [hover, setHover] = useState(null);
  const rows = useMemo(() => sortRows(cards.map(tableRow), sort.key, sort.dir), [cards, sort]);
  const today = new Date().toISOString().slice(0, 10);

  const sortBy = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const cell = (r, key) => {
    switch (key) {
      case 'site':
        return r.site ? <span style={{ fontWeight: 700, color: '#0D0D0D' }}>{r.site}</span> : <span style={{ color: '#bbb' }}>-</span>;
      case 'account':
        return <span style={{ fontWeight: r.site ? 600 : 700, color: r.site ? '#7B1FA2' : '#0D0D0D' }}>{r.account || '-'}</span>;
      case 'stage':
        return (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: STAGE_COLORS[r.stage] || '#555' }}>{r.stage}</span>
            {r.review && (
              <span style={{ ...tag, background: '#FFF3E0', color: '#E65100' }} title="Stage set by the migration - needs a review">
                review
              </span>
            )}
          </span>
        );
      case 'days':
        return r.days == null ? (
          ''
        ) : (
          <span style={{ color: r.days > 45 ? '#B71C1C' : '#333', fontWeight: r.days > 45 ? 800 : 500 }}>{r.days}</span>
        );
      case 'nda':
        return r.ndaBadge ? (
          <span style={{ ...tag, background: r.ndaBadge.bg, color: r.ndaBadge.fg }} title={r.ndaFrom ? `from ${r.ndaFrom}` : undefined}>
            {r.nda}
          </span>
        ) : (
          ''
        );
      case 'nextDue':
        if (r.nextDue)
          return <span style={{ fontWeight: 700, color: r.nextDue < today ? '#B71C1C' : '#1565C0' }}>{fmtDate(r.nextDue)}</span>;
        return r.hasTask === false ? <span style={{ ...tag, background: '#FFEBEE', color: '#B71C1C' }}>No next step</span> : '';
      case 'openOpps':
        return <span style={{ color: r.openOpps ? '#1565C0' : '#bbb', fontWeight: r.openOpps ? 800 : 500 }}>{r.openOpps}</span>;
      default:
        return r[key] || '';
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ECECEC', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #F0F0F0',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 11, color: '#888' }}>
          <b style={{ color: '#0D0D0D' }}>{rows.length}</b> {rows.length === 1 ? 'lead' : 'leads'}
        </span>
        <button
          type="button"
          disabled={rows.length === 0}
          onClick={() => exportCsv(fileName, csvRows(rows))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 14px',
            borderRadius: 8,
            border: '1px solid #E8E8E8',
            background: '#fff',
            fontSize: 11,
            fontWeight: 600,
            color: '#666',
            cursor: rows.length ? 'pointer' : 'default',
            opacity: rows.length ? 1 : 0.5,
            fontFamily: 'Montserrat',
          }}
        >
          <Icon name="download" size={13} color="#666" /> Export CSV
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat' }}>
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => {
                const on = sort.key === col.key;
                return (
                  <th
                    key={col.key}
                    style={{ ...th, textAlign: col.num ? 'right' : 'left' }}
                    aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      onClick={() => sortBy(col.key)}
                      style={{ all: 'unset', cursor: 'pointer', color: on ? '#1B5E20' : 'inherit' }}
                    >
                      {col.label}
                      {on ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                tabIndex={0}
                onClick={() => onOpen(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(r);
                }}
                onMouseEnter={() => setHover(r.id)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer', background: hover === r.id ? '#FAFBFC' : 'transparent' }}
              >
                {TABLE_COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    style={{ ...td, textAlign: col.num ? 'right' : 'left', whiteSpace: col.key === 'vertical' ? 'normal' : 'nowrap' }}
                  >
                    {cell(r, col.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 11 }}>No leads match these filters</div>
        )}
      </div>
    </div>
  );
}
